import { useMemo, useState } from "react";
import {
  FiShield,
  FiCheckCircle,
  FiAlertTriangle,
  FiPlus,
  FiRefreshCw,
  FiPlay,
  FiSlash,
  FiCheck,
  FiSearch,
  FiLayers,
  FiLock,
  FiArrowRight,
  FiX,
  FiExternalLink,
  FiGitMerge,
  FiTool,
  FiCopy,
} from "react-icons/fi";
import {
  PageContainer,
  RuntimeDiagnosticBanner,
  type ComponentDiagnostic,
} from "@swot/base-components";
import { CmtsEnterpriseModal } from "@components/CmtsEnterpriseModal.js";
import { CmtsPageHintPanel } from "@components/hints/index.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";
import type { CmtsDependencyGraphVm } from "@cmts/mappers/dependencyMappers.js";

export type CmtsMasterDependencyRow = {
  id?: string | undefined;
  code: string;
  policy: string;
  masterType: string;
  appliesTo?: string | undefined;
  sourceEntity?: string | undefined;
  targetEntity?: string | undefined;
  result: "blocked" | "warn" | "allowed" | string;
  reason?: string | undefined;
  remediation?: string | undefined;
  endpoint?: string | undefined;
  active?: boolean | undefined;
  status?: string | undefined;
  blockingCount?: number | undefined;
  warningCount?: number | undefined;
  [key: string]: unknown;
};

type ActionCardProps = {
  readonly icon: string;
  readonly title: string;
  readonly description: string;
  readonly disabled?: boolean | undefined;
  readonly danger?: boolean | undefined;
  readonly onClick: () => void;
};

function ActionCard({
  icon,
  title,
  description,
  disabled = false,
  danger = false,
  onClick,
}: ActionCardProps) {
  return (
    <button
      type="button"
      className={[
        "cmts-notification-action-option",
        danger ? "cmts-notification-action-option--danger" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled}
      onClick={onClick}
    >
      <span
        className="cmts-notification-action-option__icon"
        aria-hidden="true"
      >
        {icon}
      </span>

      <span className="cmts-notification-action-option__content">
        <strong>{title}</strong>
        <small>{description}</small>
      </span>

      <span
        className="cmts-notification-action-option__arrow"
        aria-hidden="true"
      >
        ›
      </span>
    </button>
  );
}

const DEFAULT_DEPENDENCY_ROWS: readonly CmtsMasterDependencyRow[] = [
  {
    id: "dep-rule-01",
    code: "DEP_QUALITY_ROUTING_BLOCK",
    policy: "Priority Routing Dependency on Quality Category",
    masterType: "CATEGORY",
    appliesTo: "workflow",
    sourceEntity: "Category: QUALITY",
    targetEntity: "RoutingRule: ROUTE-CRITICAL-CUSTOMER",
    result: "blocked",
    reason:
      "Active complaint routing rules depend on this quality category code. Provide replacement routing rule before deactivating.",
    remediation:
      "Reclassify active complaints or assign an active fallback routing rule before deactivating category.",
    endpoint: "/api/cmts/master-dependencies/check-deactivate",
    active: true,
    status: "Active",
  },
  {
    id: "dep-rule-02",
    code: "DEP_SEV1_SLA_SCHEDULE_BLOCK",
    policy: "SLA Calendar Schedule Mapping Dependency",
    masterType: "CALENDAR",
    appliesTo: "sla",
    sourceEntity: "Calendar: 24x7 Shift",
    targetEntity: "SLA Tier: CRITICAL_SEV1_SLA",
    result: "blocked",
    reason:
      "Active tier-1 complaints are bound to this SLA calendar profile. Reassign active complaints before deletion.",
    remediation:
      "Map active complaints to standard business calendar before executing shift calendar deletion.",
    endpoint: "/api/cmts/master-dependencies/check-delete",
    active: true,
    status: "Active",
  },
  {
    id: "dep-rule-03",
    code: "DEP_ROLE_INVESTIGATOR_WARN",
    policy: "Lead Investigator Persona Approval Workflow Link",
    masterType: "ROLE",
    appliesTo: "access",
    sourceEntity: "Role: Lead Investigator",
    targetEntity: "Stage: Root Cause Verification",
    result: "warn",
    reason:
      "Role has 12 assigned in-progress complaints. Deactivation triggers audit warning and mandatory reassignment.",
    remediation:
      "Run batch reassignment to transfer active RCA work orders to a qualified investigator.",
    endpoint: "/api/cmts/master-dependencies/check-deactivate",
    active: true,
    status: "Active",
  },
  {
    id: "dep-rule-04",
    code: "DEP_NOTIF_TEMPLATE_SMS_WARN",
    policy: "SMS Notification Template Provider Binding",
    masterType: "NOTIFICATION_TEMPLATE",
    appliesTo: "notification",
    sourceEntity: "Template: Stage Escalation",
    targetEntity: "Provider: Twilio Production SMS",
    result: "warn",
    reason:
      "SMS fallback provider is configured. Template modification will trigger provider format verification.",
    remediation:
      "Verify SMS character limits and variables against the active SMS provider webhook.",
    endpoint: "/api/cmts/master-dependencies/check",
    active: true,
    status: "Active",
  },
  {
    id: "dep-rule-05",
    code: "DEP_EVIDENCE_RETENTION_RULE_BLOCK",
    policy: "Regulatory Evidence Retention Rule Binding",
    masterType: "EVIDENCE_RULE",
    appliesTo: "evidence",
    sourceEntity: "Policy: 7-Year Retention",
    targetEntity: "Evidence Vault: Secure S3 Bucket",
    result: "blocked",
    reason:
      "34 compliant evidence packages are sealed under this rule. Deletion is strictly blocked under compliance policy.",
    remediation:
      "Archive sealed evidence packages to permanent secondary vault before unbinding retention policy.",
    endpoint: "/api/cmts/master-dependencies/check-delete",
    active: true,
    status: "Active",
  },
  {
    id: "dep-rule-06",
    code: "DEP_CONFIG_VERSION_ACTIVATION_ALLOWED",
    policy: "Governance Configuration Version v2.4 Integrity",
    masterType: "CONFIGURATION",
    appliesTo: "configuration",
    sourceEntity: "Draft: v2.4 Schema",
    targetEntity: "Production: Live Runtime",
    result: "allowed",
    reason:
      "All 18 dependency nodes and circular checks verified clean. Ready for zero-downtime activation.",
    remediation:
      "No blocking conditions found. Proceed with production version activation.",
    endpoint: "/api/cmts/master-dependencies/check-activation",
    active: true,
    status: "Active",
  },
];

export const DEFAULT_MASTER_ENTITY_OPTIONS: Record<
  string,
  readonly { code: string; label: string }[]
> = {
  CATEGORY: [
    {
      code: "PRODUCT_QUALITY_COMPLAINT",
      label: "PRODUCT_QUALITY_COMPLAINT - Product Quality Complaint",
    },
    {
      code: "CUSTOMER_COMPLAINT_FIELD_FAILURE",
      label:
        "CUSTOMER_COMPLAINT_FIELD_FAILURE - Customer Complaint / Field Failure",
    },
    {
      code: "SUPPLIER_QUALITY_COMPLAINT",
      label: "SUPPLIER_QUALITY_COMPLAINT - Supplier Quality Complaint",
    },
    { code: "SAFETY_COMPLAINT", label: "SAFETY_COMPLAINT - Safety Complaint" },
    {
      code: "ERP_SYSTEM_COMPLAINT",
      label: "ERP_SYSTEM_COMPLAINT - ERP / System Complaint",
    },
    {
      code: "COMMERCIAL_COMPLAINT",
      label: "COMMERCIAL_COMPLAINT - Commercial Complaint",
    },
  ],
  ROLE: [
    {
      code: "COMPLAINT_CREATOR",
      label: "COMPLAINT_CREATOR - Complaint Creator",
    },
    { code: "COMPLAINT_OWNER", label: "COMPLAINT_OWNER - Complaint Owner" },
    {
      code: "FUNCTIONAL_EXPERT",
      label: "FUNCTIONAL_EXPERT - Functional Expert",
    },
    { code: "QA_MANAGER", label: "QA_MANAGER - QA Manager" },
    { code: "SITE_HEAD", label: "SITE_HEAD - Site Head" },
    { code: "PLATFORM_ADMIN", label: "PLATFORM_ADMIN - Platform Admin" },
    { code: "AUDIT_OFFICER", label: "AUDIT_OFFICER - Audit Officer" },
  ],
  CALENDAR: [
    {
      code: "STANDARD_BUSINESS_HOURS",
      label:
        "STANDARD_BUSINESS_HOURS - Standard Business Hours (09:00 - 18:00)",
    },
    {
      code: "24X7_CONTINUOUS",
      label: "24X7_CONTINUOUS - 24x7 Continuous Manufacturing Calendar",
    },
    {
      code: "PLANT_SHIFT_A",
      label: "PLANT_SHIFT_A - Plant Shift A (06:00 - 14:00)",
    },
    {
      code: "PLANT_SHIFT_B",
      label: "PLANT_SHIFT_B - Plant Shift B (14:00 - 22:00)",
    },
  ],
  WORKFLOW_STATUS: [
    { code: "DRAFT", label: "DRAFT - Draft" },
    { code: "SUBMITTED", label: "SUBMITTED - Submitted" },
    {
      code: "UNDER_INVESTIGATION",
      label: "UNDER_INVESTIGATION - Under Investigation",
    },
    { code: "RCA_IN_PROGRESS", label: "RCA_IN_PROGRESS - RCA In Progress" },
    { code: "CAPA_PENDING", label: "CAPA_PENDING - CAPA Pending" },
    { code: "RESOLVED", label: "RESOLVED - Resolved" },
    { code: "CLOSED", label: "CLOSED - Closed" },
  ],
  WORKFLOW_ACTION: [
    { code: "SUBMIT", label: "SUBMIT - Submit Complaint" },
    { code: "ASSIGN", label: "ASSIGN - Assign Owner" },
    { code: "INVESTIGATE", label: "INVESTIGATE - Investigate Root Cause" },
    { code: "CAPA_CREATE", label: "CAPA_CREATE - Initiate CAPA" },
    { code: "VERIFY", label: "VERIFY - Verify Action Effectiveness" },
    { code: "CLOSE", label: "CLOSE - Approve & Close Complaint" },
  ],
  NOTIFICATION_TEMPLATE: [
    {
      code: "COMPLAINT_CREATED_ACK",
      label: "COMPLAINT_CREATED_ACK - Complaint Created Acknowledgment",
    },
    {
      code: "SLA_BREACH_WARNING",
      label: "SLA_BREACH_WARNING - SLA Breach Warning",
    },
    {
      code: "ESCALATION_LEVEL_1",
      label: "ESCALATION_LEVEL_1 - Escalation Level 1",
    },
    {
      code: "CLOSURE_VERIFICATION_REQUIRED",
      label: "CLOSURE_VERIFICATION_REQUIRED - Closure Verification Required",
    },
  ],
  EVIDENCE_RULE: [
    {
      code: "EV_SEV1_MANDATORY_PHOTO",
      label: "EV_SEV1_MANDATORY_PHOTO - Mandatory Defect Photo Rule",
    },
    {
      code: "EV_CAPA_ATTACHMENT_REQ",
      label: "EV_CAPA_ATTACHMENT_REQ - CAPA Verification Evidence",
    },
    {
      code: "EV_REGULATORY_REPORT_PACK",
      label: "EV_REGULATORY_REPORT_PACK - Regulatory Compliance Package",
    },
  ],
  REPORT_CONFIGURATION: [
    {
      code: "COMPLAINT_SLA_COMPLIANCE",
      label: "COMPLAINT_SLA_COMPLIANCE - Complaint SLA Compliance Report",
    },
    {
      code: "EXECUTIVE_QUALITY_SUMMARY",
      label: "EXECUTIVE_QUALITY_SUMMARY - Executive Quality Summary",
    },
    {
      code: "MONTHLY_CAPA_STATUS",
      label: "MONTHLY_CAPA_STATUS - Monthly CAPA Status Report",
    },
  ],
  NOTIFICATION_PROVIDER: [
    {
      code: "SMTP_PRIMARY",
      label: "SMTP_PRIMARY - Corporate SMTP Mail Provider",
    },
    { code: "SLACK_ALERTS", label: "SLACK_ALERTS - Slack Webhook Broadcast" },
    { code: "SMS_GATEWAY", label: "SMS_GATEWAY - Enterprise SMS Gateway" },
  ],
};

export function CmtsMasterDependencyPage(props: {
  graph?: CmtsDependencyGraphVm | null | undefined;
  rows?: readonly Record<string, unknown>[] | undefined;
  diagnostics?: readonly ComponentDiagnostic[] | undefined;
  masterOptions?:
    | Record<
        string,
        readonly {
          code?: string;
          value?: string;
          label?: string;
          name?: string;
        }[]
      >
    | undefined;
  onOpenLinked?:
    ((row: Record<string, unknown>) => void | Promise<void>) | undefined;
  onRemediate?:
    ((row: Record<string, unknown>) => void | Promise<void>) | undefined;
  onRunCheck?:
    ((payload: Record<string, unknown>) => void | Promise<void>) | undefined;
  onRefresh?: (() => void | Promise<void>) | undefined;
}) {
  const rawRows: readonly CmtsMasterDependencyRow[] = useMemo(() => {
    if (props.rows !== undefined) {
      return props.rows.map((r, i) => {
        const code = String(
          r.code ?? r.id ?? `DEP_RECORD_${i + 1}`,
        ).toUpperCase();
        const policy = String(
          r.policy ?? r.operation ?? r.name ?? r.title ?? code,
        ).trim();
        const masterType = String(
          r.masterType ?? r.entityType ?? "CATEGORY",
        ).toUpperCase();
        const appliesTo = String(
          r.appliesTo ??
            r.domain ??
            (masterType.includes("ROLE")
              ? "access"
              : masterType.includes("CALENDAR")
                ? "sla"
                : masterType.includes("EVIDENCE")
                  ? "evidence"
                  : masterType.includes("NOTIF")
                    ? "notification"
                    : "workflow"),
        ).toLowerCase();
        const isBlock =
          String(r.block ?? "").toLowerCase() === "yes" ||
          r.blocking === true ||
          String(r.impactType ?? "").toLowerCase() === "blocking";
        const result = String(
          r.result ?? (isBlock ? "blocked" : "warn"),
        ).toLowerCase();
        const active =
          r.active !== false &&
          String(r.status ?? "").toLowerCase() !== "inactive";
        return {
          ...r,
          code,
          policy,
          masterType,
          appliesTo,
          sourceEntity: String(
            r.sourceEntity ??
              r.entityId ??
              r.sourceEntityId ??
              `${masterType}: PRODUCT_QUALITY_COMPLAINT`,
          ),
          targetEntity: String(
            r.targetEntity ??
              r.targetEntityId ??
              r.impactedEntityId ??
              "Workflow Routing",
          ),
          result:
            result === "yes" || result === "blocking" ? "blocked" : result,
          reason: String(
            r.reason ??
              r.description ??
              r.remediationRecommendation ??
              "Master dependency condition evaluated.",
          ).trim(),
          remediation: String(
            r.remediation ??
              r.remediationRecommendation ??
              "Review linked entities and provide active replacements.",
          ).trim(),
          endpoint: String(r.endpoint ?? "/api/cmts/master-dependencies/check"),
          active,
          status: active ? "Active" : "Inactive",
        };
      });
    }
    return DEFAULT_DEPENDENCY_ROWS;
  }, [props.rows]);

  // Filters & State
  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState<string>("ALL");
  const [selectedResult, setSelectedResult] = useState<string>("ALL");
  const [selectedStatus, setSelectedStatus] = useState<string>("ALL");

  // Action Drawer / Modal State
  const [actionsFor, setActionsFor] = useState<CmtsMasterDependencyRow | null>(
    null,
  );

  // Run Check / Create Modal State
  const [formOpen, setFormOpen] = useState(false);
  const [formMasterType, setFormMasterType] = useState("CATEGORY");
  const [formMasterCode, setFormMasterCode] = useState(
    "PRODUCT_QUALITY_COMPLAINT",
  );
  const [formOperation, setFormOperation] = useState("READ_IMPACT");
  const [formRemarks, setFormRemarks] = useState("");

  // Simulator Modal State
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testRow, setTestRow] = useState<CmtsMasterDependencyRow | null>(null);
  const [testType, setTestType] = useState("CATEGORY");
  const [testCode, setTestCode] = useState("PRODUCT_QUALITY_COMPLAINT");
  const [testOp, setTestOp] = useState("DEACTIVATE_CHECK");
  const [simResult, setSimResult] = useState<{
    status: "blocked" | "allowed" | "warn";
    title: string;
    message: string;
    remediation?: string;
  } | null>(null);

  // Remediation Modal State
  const [remediateModalOpen, setRemediateModalOpen] = useState(false);
  const [remediateRow, setRemediateRow] =
    useState<CmtsMasterDependencyRow | null>(null);

  // Graph Details Modal State
  const [graphModalOpen, setGraphModalOpen] = useState(false);

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Execute Remediation
  const handleExecuteRemediation = async (row: CmtsMasterDependencyRow) => {
    setBusy(true);
    try {
      if (props.onRemediate) {
        await props.onRemediate(row);
      }
      setMessage(`Remediation action successfully executed for [${row.code}].`);
      setRemediateModalOpen(false);
      setActionsFor(null);
      if (props.onRefresh) await props.onRefresh();
    } catch (e) {
      setMessage(
        normalizeUserSafeApiError(e).userMessage ||
          "Failed to execute remediation.",
      );
    } finally {
      setBusy(false);
    }
  };

  // Run Live Check
  const handleRunDependencyCheck = async () => {
    setBusy(true);
    try {
      if (props.onRunCheck) {
        await props.onRunCheck({
          masterType: formMasterType,
          masterCode: formMasterCode.trim().toUpperCase(),
          operation: formOperation,
          remarks: formRemarks.trim(),
        });
      }
      setMessage(
        `Dependency check completed for ${formMasterType} [${formMasterCode.toUpperCase()}].`,
      );
      setFormOpen(false);
      if (props.onRefresh) await props.onRefresh();
    } catch (e) {
      setMessage(
        normalizeUserSafeApiError(e).userMessage || "Dependency check failed.",
      );
    } finally {
      setBusy(false);
    }
  };

  // Run Simulator
  const runSimulation = () => {
    const target = testRow;
    if (target && target.result === "blocked") {
      setSimResult({
        status: "blocked",
        title: `Operation Blocked: [${target.code}]`,
        message:
          target.reason ||
          `Active downstream dependency prevents this master operation.`,
        remediation:
          target.remediation ||
          `Provide replacement routing / SLA rule before deactivation.`,
      });
    } else if (target && target.result === "warn") {
      setSimResult({
        status: "warn",
        title: `Audit Warning: [${target.code}]`,
        message:
          target.reason ||
          `Operation allowed with mandatory compliance audit trail.`,
        remediation:
          target.remediation ||
          `Review linked records before completing change.`,
      });
    } else {
      setSimResult({
        status: "allowed",
        title: "Integrity Verified: Ready for Operation",
        message: `Zero blocking cycles or orphaned records detected for ${testType} [${testCode}].`,
        remediation: "All dependency constraints satisfied. Ready to proceed.",
      });
    }
  };

  // Filtered Rows
  const filteredRows = useMemo(() => {
    return rawRows.filter((r) => {
      const matchesSearch =
        !search.trim() ||
        r.code.toLowerCase().includes(search.toLowerCase()) ||
        r.policy.toLowerCase().includes(search.toLowerCase()) ||
        String(r.masterType ?? "")
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        String(r.sourceEntity ?? "")
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        String(r.targetEntity ?? "")
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        String(r.reason ?? "")
          .toLowerCase()
          .includes(search.toLowerCase());

      const matchesType =
        selectedType === "ALL" ||
        r.masterType.toUpperCase() === selectedType.toUpperCase();

      const matchesResult =
        selectedResult === "ALL" ||
        r.result.toLowerCase() === selectedResult.toLowerCase();

      const matchesStatus =
        selectedStatus === "ALL" ||
        (selectedStatus === "ACTIVE" && r.active) ||
        (selectedStatus === "INACTIVE" && !r.active);

      return matchesSearch && matchesType && matchesResult && matchesStatus;
    });
  }, [rawRows, search, selectedType, selectedResult, selectedStatus]);

  // Metrics
  const totalDeps = rawRows.length;
  const blockingDeps = rawRows.filter((r) => r.result === "blocked").length;
  const warningDeps = rawRows.filter((r) => r.result === "warn").length;
  const allowedDeps = rawRows.filter((r) => r.result === "allowed").length;

  return (
    <PageContainer
      title="Master Dependency Graph"
      subtitle="Comprehensive cross-module entity dependency analysis, delete/deactivate impact prevention, and automated remediation."
    >
      <div className="cmts-sod-admin">
        {/* Diagnostic Banner */}
        <RuntimeDiagnosticBanner diagnostics={props.diagnostics ?? []} />
        <CmtsPageHintPanel defaultOpen={false}>
          Dependency graph checks evaluate referential integrity across master-data entities, preventing orphan records and blocking unsafe deactivations.
        </CmtsPageHintPanel>

        {/* Hero Header */}
        <section className="cmts-workspace-hero">
          <div>
            <p className="cmts-section-eyebrow">
              Governance &amp; Dependency Graph
            </p>
            <h2>Master Dependency Analysis &amp; Readiness</h2>
            <p>
              Evaluate cross-module integrity, prevent orphaned records during
              delete/deactivate operations, analyze circular references, and
              enforce readiness sign-offs before configuration changes.
            </p>
          </div>

          <div className="cmts-page-actions">
            <button
              type="button"
              className="cmts-action-button"
              onClick={() => {
                setFormMasterType("CATEGORY");
                setFormMasterCode("QUALITY");
                setFormOperation("READ_IMPACT");
                setFormRemarks("");
                setFormOpen(true);
              }}
            >
              <FiPlus /> Run Dependency Check
            </button>
            <button
              type="button"
              className="cmts-secondary-button"
              onClick={() => {
                setTestRow(rawRows[0] || null);
                setTestType(rawRows[0]?.masterType || "CATEGORY");
                setTestCode(rawRows[0]?.code || "QUALITY");
                setSimResult(null);
                setTestModalOpen(true);
              }}
            >
              <FiPlay /> Test Simulation
            </button>
            <button
              type="button"
              className="cmts-secondary-button"
              onClick={async () => {
                if (props.onRefresh) await props.onRefresh();
                setMessage("Master dependency graph refreshed.");
              }}
            >
              <FiRefreshCw /> Refresh
            </button>
          </div>
        </section>

        {/* Status Toast Message */}
        {message && (
          <div
            style={{
              padding: "0.85rem 1.25rem",
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 10,
              color: "#166534",
              fontSize: "0.875rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{message}</span>
            <button
              type="button"
              onClick={() => setMessage(null)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#166534",
              }}
            >
              <FiX />
            </button>
          </div>
        )}

        {/* KPI Metrics */}
        <section className="cmts-sod-admin__kpis">
          <div className="cmts-sod-kpi">
            <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--blue">
              <FiLayers />
            </div>
            <div className="cmts-sod-kpi__body">
              <span className="cmts-sod-kpi__label">Governed Dependencies</span>
              <div className="cmts-sod-kpi__val-row">
                <span className="cmts-sod-kpi__value">{totalDeps}</span>
                <span className="cmts-sod-kpi__sub">Nodes & Edges</span>
              </div>
            </div>
          </div>

          <div className="cmts-sod-kpi">
            <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--rose">
              <FiLock />
            </div>
            <div className="cmts-sod-kpi__body">
              <span className="cmts-sod-kpi__label">Blocking Hard Stops</span>
              <div className="cmts-sod-kpi__val-row">
                <span className="cmts-sod-kpi__value">{blockingDeps}</span>
                <span className="cmts-sod-kpi__sub">Strict Blockers</span>
              </div>
            </div>
          </div>

          <div className="cmts-sod-kpi">
            <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--amber">
              <FiAlertTriangle />
            </div>
            <div className="cmts-sod-kpi__body">
              <span className="cmts-sod-kpi__label">Audit Warnings</span>
              <div className="cmts-sod-kpi__val-row">
                <span className="cmts-sod-kpi__value">{warningDeps}</span>
                <span className="cmts-sod-kpi__sub">Impact Logged</span>
              </div>
            </div>
          </div>

          <div className="cmts-sod-kpi">
            <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--emerald">
              <FiCheckCircle />
            </div>
            <div className="cmts-sod-kpi__body">
              <span className="cmts-sod-kpi__label">Readiness Health</span>
              <div className="cmts-sod-kpi__val-row">
                <span className="cmts-sod-kpi__value">
                  {totalDeps
                    ? `${Math.round(((totalDeps - blockingDeps) / totalDeps) * 100)}%`
                    : "100%"}
                </span>
                <span className="cmts-sod-kpi__sub">Clean Cycles</span>
              </div>
            </div>
          </div>
        </section>

        {/* Controls: Filter Pills + Search */}
        <section className="cmts-sod-admin__controls">
          <div className="cmts-sod-admin__filter-row">
            <div className="cmts-sod-admin__domain-pills">
              {[
                { id: "ALL", label: "All Types" },
                { id: "CATEGORY", label: "Category" },
                { id: "CALENDAR", label: "Calendar" },
                { id: "ROLE", label: "Role" },
                { id: "EVIDENCE_RULE", label: "Evidence" },
                { id: "CONFIGURATION", label: "Configuration" },
                { id: "NOTIFICATION_TEMPLATE", label: "Notification" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`cmts-sod-pill ${selectedType === tab.id ? "cmts-sod-pill--active" : ""}`}
                  onClick={() => setSelectedType(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="cmts-sod-admin__search-box">
              <FiSearch style={{ color: "#94a3b8" }} />
              <input
                type="text"
                placeholder="Search dependencies by code, entity, reason, or endpoint..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#94a3b8",
                  }}
                >
                  <FiX />
                </button>
              )}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "1rem",
              alignItems: "center",
              fontSize: "0.82rem",
              color: "#64748b",
            }}
          >
            <span>
              <strong>Impact Result:</strong>
            </span>
            <button
              type="button"
              onClick={() => setSelectedResult("ALL")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontWeight: selectedResult === "ALL" ? 700 : 500,
                color: selectedResult === "ALL" ? "#0f172a" : "#64748b",
              }}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setSelectedResult("blocked")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontWeight: selectedResult === "blocked" ? 700 : 500,
                color: selectedResult === "blocked" ? "#be123c" : "#64748b",
              }}
            >
              Blocked Only
            </button>
            <button
              type="button"
              onClick={() => setSelectedResult("warn")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontWeight: selectedResult === "warn" ? 700 : 500,
                color: selectedResult === "warn" ? "#c2410c" : "#64748b",
              }}
            >
              Warn Only
            </button>
            <button
              type="button"
              onClick={() => setSelectedResult("allowed")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontWeight: selectedResult === "allowed" ? 700 : 500,
                color: selectedResult === "allowed" ? "#047857" : "#64748b",
              }}
            >
              Allowed Only
            </button>

            <span style={{ marginLeft: "1.5rem" }}>
              <strong>Status:</strong>
            </span>
            <button
              type="button"
              onClick={() => setSelectedStatus("ALL")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontWeight: selectedStatus === "ALL" ? 700 : 500,
                color: selectedStatus === "ALL" ? "#0f172a" : "#64748b",
              }}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setSelectedStatus("ACTIVE")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontWeight: selectedStatus === "ACTIVE" ? 700 : 500,
                color: selectedStatus === "ACTIVE" ? "#047857" : "#64748b",
              }}
            >
              Active Only
            </button>
            <button
              type="button"
              onClick={() => setSelectedStatus("INACTIVE")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontWeight: selectedStatus === "INACTIVE" ? 700 : 500,
                color: selectedStatus === "INACTIVE" ? "#64748b" : "#94a3b8",
              }}
            >
              Inactive Only
            </button>
          </div>
        </section>

        {/* Data Table Card */}
        <section className="cmts-sod-table-card">
          <table className="cmts-sod-table">
            <thead>
              <tr>
                <th>Dependency & Code</th>
                <th>Master Type</th>
                <th>Entity Flow / Mapping</th>
                <th>Impact Result</th>
                <th>Status</th>
                <th style={{ textAlign: "center", width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      textAlign: "center",
                      padding: "3rem",
                      color: "#64748b",
                    }}
                  >
                    <FiShield
                      style={{
                        fontSize: "2.5rem",
                        color: "#cbd5e1",
                        display: "block",
                        margin: "0 auto 0.75rem",
                      }}
                    />
                    <strong>
                      No master dependencies matched your filters.
                    </strong>
                    <p style={{ margin: "0.35rem 0 0", fontSize: "0.82rem" }}>
                      Try adjusting your search criteria or run a new dependency
                      check.
                    </p>
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => {
                  const domainClass =
                    row.appliesTo === "sla"
                      ? "cmts-sod-badge--domain-closure"
                      : row.appliesTo === "access"
                        ? "cmts-sod-badge--domain-verification"
                        : row.appliesTo === "configuration"
                          ? "cmts-sod-badge--domain-config"
                          : row.appliesTo === "evidence"
                            ? "cmts-sod-badge--domain-verification"
                            : "cmts-sod-badge--domain-approval";

                  const resultClass =
                    row.result === "blocked"
                      ? "cmts-sod-badge--result-blocked"
                      : row.result === "warn"
                        ? "cmts-sod-badge--result-warn"
                        : "cmts-sod-badge--status-active";

                  return (
                    <tr key={row.code}>
                      <td>
                        <div className="cmts-sod-policy-cell">
                          <strong>{row.policy}</strong>
                          <code>{row.code}</code>
                          <span className="cmts-sod-policy-desc">
                            {row.reason}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`cmts-sod-badge ${domainClass}`}>
                          {row.masterType}
                        </span>
                      </td>
                      <td>
                        <div className="cmts-sod-constraint-tag">
                          <span>{row.sourceEntity || "Source"}</span>
                          <FiArrowRight style={{ color: "#94a3b8" }} />
                          <strong>{row.targetEntity || "Target"}</strong>
                        </div>
                      </td>
                      <td>
                        <span className={`cmts-sod-badge ${resultClass}`}>
                          {row.result === "blocked" ? (
                            <FiLock />
                          ) : row.result === "warn" ? (
                            <FiAlertTriangle />
                          ) : (
                            <FiCheck />
                          )}
                          {row.result === "blocked"
                            ? "BLOCKED"
                            : row.result === "warn"
                              ? "WARN (AUDITED)"
                              : "ALLOWED"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`cmts-sod-badge ${row.active ? "cmts-sod-badge--status-active" : "cmts-sod-badge--status-inactive"}`}
                        >
                          {row.active ? <FiCheck /> : <FiSlash />}
                          {row.status}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          type="button"
                          className="cmts-notification-actions-button"
                          aria-label={`Open actions for ${row.code}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            setActionsFor(row);
                          }}
                        >
                          <span
                            className="cmts-notification-actions-button__icon"
                            aria-hidden="true"
                          >
                            ⋮
                          </span>
                          <span>Actions</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </section>

        {/* =========================================================================
            Dependency Actions Popup Modal
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={Boolean(actionsFor)}
          title="Dependency actions"
          subtitle={
            actionsFor ? `${actionsFor.code} • ${actionsFor.policy}` : ""
          }
          size="md"
          onClose={() => setActionsFor(null)}
        >
          {actionsFor && (
            <div className="cmts-notification-action-modal">
              {/* Metadata Summary Box */}
              <div className="cmts-notification-action-modal__summary">
                <div>
                  <span>Master Type</span>
                  <strong style={{ color: "#0284c7" }}>
                    {actionsFor.masterType}
                  </strong>
                </div>

                <div>
                  <span>Enforcement Impact</span>
                  <strong
                    style={{
                      color:
                        actionsFor.result === "blocked"
                          ? "#be123c"
                          : actionsFor.result === "warn"
                            ? "#c2410c"
                            : "#059669",
                    }}
                  >
                    {actionsFor.result?.toUpperCase() ?? "ALLOWED"}
                  </strong>
                </div>

                <div>
                  <span>Status</span>
                  <strong
                    style={{ color: actionsFor.active ? "#059669" : "#64748b" }}
                  >
                    {actionsFor.status?.toUpperCase() ?? "ACTIVE"}
                  </strong>
                </div>
              </div>

              {/* Action Cards Grid */}
              <div className="cmts-notification-action-modal__grid">
                <ActionCard
                  icon="⚡"
                  title="Test dependency simulation"
                  description="Evaluate live deletion or deactivation impacts against this rule."
                  onClick={() => {
                    const target = actionsFor;
                    setActionsFor(null);
                    setTestRow(target);
                    setTestType(target.masterType);
                    setTestCode(target.code);
                    setSimResult(null);
                    setTestModalOpen(true);
                  }}
                />

                <ActionCard
                  icon="🔍"
                  title="Inspect graph trace"
                  description="View source, target node references, and blocking closure paths."
                  onClick={() => {
                    setGraphModalOpen(true);
                  }}
                />

                <ActionCard
                  icon="🛠"
                  title="Remediate dependency"
                  description="Review remediation recommendations and execute mitigation."
                  onClick={() => {
                    const target = actionsFor;
                    setActionsFor(null);
                    setRemediateRow(target);
                    setRemediateModalOpen(true);
                  }}
                />

                <ActionCard
                  icon="📋"
                  title="Copy dependency trace"
                  description="Copy structured dependency context and diagnostic identifiers."
                  onClick={() => {
                    const summary = JSON.stringify(actionsFor, null, 2);
                    void navigator.clipboard?.writeText(summary);
                    setMessage(
                      `Dependency trace copied to clipboard for [${actionsFor.code}].`,
                    );
                    setActionsFor(null);
                  }}
                />
              </div>

              {/* Danger / Remediation Priority Action Card at Bottom */}
              <div className="cmts-notification-action-modal__danger">
                <ActionCard
                  icon={actionsFor.result === "blocked" ? "⚠" : "✓"}
                  title={
                    actionsFor.result === "blocked"
                      ? "Execute blocking remediation"
                      : "Verify readiness status"
                  }
                  description={
                    actionsFor.result === "blocked"
                      ? "Resolve active downstream bindings to unblock master lifecycle change."
                      : "Confirm all integrity checks are compliant before deployment."
                  }
                  danger={actionsFor.result === "blocked"}
                  onClick={() => {
                    const target = actionsFor;
                    setActionsFor(null);
                    setRemediateRow(target);
                    setRemediateModalOpen(true);
                  }}
                />
              </div>
            </div>
          )}
        </CmtsEnterpriseModal>

        {/* =========================================================================
            Run Dependency Check Modal
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={formOpen}
          title="Run Master Dependency Evaluation"
          subtitle="Analyze cross-module impacts, circular references, and readiness constraints"
          size="lg"
          onClose={() => setFormOpen(false)}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1.25rem",
              padding: "0.5rem",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    color: "#334155",
                    marginBottom: "0.35rem",
                  }}
                >
                  Master Entity Type *
                </label>
                <select
                  value={formMasterType}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setFormMasterType(nextType);
                    const opts =
                      props.masterOptions?.[nextType] ??
                      DEFAULT_MASTER_ENTITY_OPTIONS[nextType] ??
                      [];
                    if (opts.length > 0) {
                      setFormMasterCode(
                        String(
                          (opts[0] as any).code ?? (opts[0] as any).value ?? "",
                        ),
                      );
                    }
                  }}
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.85rem",
                    border: "1px solid #cbd5e1",
                    borderRadius: 8,
                    fontSize: "0.875rem",
                  }}
                >
                  <option value="CATEGORY">
                    CATEGORY (Complaint / Routing Category)
                  </option>
                  <option value="ROLE">ROLE (Governance Persona)</option>
                  <option value="CALENDAR">
                    CALENDAR (SLA & Shift Calendar)
                  </option>
                  <option value="WORKFLOW_STATUS">
                    WORKFLOW_STATUS (Complaint Lifecycle)
                  </option>
                  <option value="WORKFLOW_ACTION">
                    WORKFLOW_ACTION (Transition Action)
                  </option>
                  <option value="NOTIFICATION_TEMPLATE">
                    NOTIFICATION_TEMPLATE (Message Rule)
                  </option>
                  <option value="EVIDENCE_RULE">
                    EVIDENCE_RULE (Compliance Vault)
                  </option>
                  <option value="REPORT_CONFIGURATION">
                    REPORT_CONFIGURATION (Analytics Export)
                  </option>
                  <option value="NOTIFICATION_PROVIDER">
                    NOTIFICATION_PROVIDER (Dispatch Gateway)
                  </option>
                </select>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    color: "#334155",
                    marginBottom: "0.35rem",
                  }}
                >
                  Master Record Code *
                </label>
                <select
                  value={formMasterCode}
                  onChange={(e) => setFormMasterCode(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.85rem",
                    border: "1px solid #cbd5e1",
                    borderRadius: 8,
                    fontSize: "0.875rem",
                  }}
                >
                  {(
                    props.masterOptions?.[formMasterType] ??
                    DEFAULT_MASTER_ENTITY_OPTIONS[formMasterType] ??
                    []
                  ).map((opt) => {
                    const val = String(
                      (opt as any).code ?? (opt as any).value ?? "",
                    );
                    const lbl = String(
                      (opt as any).label ?? (opt as any).name ?? val,
                    );
                    return (
                      <option key={val} value={val}>
                        {lbl}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    color: "#334155",
                    marginBottom: "0.35rem",
                  }}
                >
                  Evaluation Operation *
                </label>
                <select
                  value={formOperation}
                  onChange={(e) => setFormOperation(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.85rem",
                    border: "1px solid #cbd5e1",
                    borderRadius: 8,
                    fontSize: "0.875rem",
                  }}
                >
                  <option value="READ_IMPACT">
                    READ_IMPACT (General Dependency Analysis)
                  </option>
                  <option value="DEACTIVATE_CHECK">
                    DEACTIVATE_CHECK (Pre-Deactivation Readiness)
                  </option>
                  <option value="DELETE_CHECK">
                    DELETE_CHECK (Pre-Deletion Safety Check)
                  </option>
                  <option value="ACTIVATION_CHECK">
                    ACTIVATION_CHECK (Configuration Activation)
                  </option>
                  <option value="ROLLBACK_CHECK">
                    ROLLBACK_CHECK (Rollback Safety Check)
                  </option>
                </select>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    color: "#334155",
                    marginBottom: "0.35rem",
                  }}
                >
                  Analysis Scope / Remarks
                </label>
                <input
                  type="text"
                  value={formRemarks}
                  onChange={(e) => setFormRemarks(e.target.value)}
                  placeholder="e.g. Pre-release governance audit"
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.85rem",
                    border: "1px solid #cbd5e1",
                    borderRadius: 8,
                    fontSize: "0.875rem",
                  }}
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.75rem",
                marginTop: "0.75rem",
              }}
            >
              <button
                type="button"
                className="cmts-sod-btn cmts-sod-btn--secondary"
                onClick={() => setFormOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cmts-sod-btn cmts-sod-btn--primary"
                disabled={busy || !formMasterCode.trim()}
                onClick={handleRunDependencyCheck}
              >
                {busy ? "Evaluating Graph..." : "Execute Analysis"}
              </button>
            </div>
          </div>
        </CmtsEnterpriseModal>

        {/* =========================================================================
            Test Simulation Modal
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={testModalOpen}
          title="Dependency Impact Simulator"
          subtitle="Simulate lifecycle operations against active governance closure rules"
          size="lg"
          onClose={() => setTestModalOpen(false)}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1.25rem",
              padding: "0.5rem",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    color: "#334155",
                    marginBottom: "0.35rem",
                  }}
                >
                  Master Entity Type
                </label>
                <select
                  value={testType}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setTestType(nextType);
                    const opts =
                      props.masterOptions?.[nextType] ??
                      DEFAULT_MASTER_ENTITY_OPTIONS[nextType] ??
                      [];
                    if (opts.length > 0) {
                      setTestCode(
                        String(
                          (opts[0] as any).code ?? (opts[0] as any).value ?? "",
                        ),
                      );
                    }
                  }}
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.85rem",
                    border: "1px solid #cbd5e1",
                    borderRadius: 8,
                    fontSize: "0.875rem",
                  }}
                >
                  <option value="CATEGORY">
                    CATEGORY (Complaint / Routing Category)
                  </option>
                  <option value="ROLE">ROLE (Governance Persona)</option>
                  <option value="CALENDAR">
                    CALENDAR (SLA & Shift Calendar)
                  </option>
                  <option value="WORKFLOW_STATUS">
                    WORKFLOW_STATUS (Complaint Lifecycle)
                  </option>
                  <option value="WORKFLOW_ACTION">
                    WORKFLOW_ACTION (Transition Action)
                  </option>
                  <option value="NOTIFICATION_TEMPLATE">
                    NOTIFICATION_TEMPLATE (Message Rule)
                  </option>
                  <option value="EVIDENCE_RULE">
                    EVIDENCE_RULE (Compliance Vault)
                  </option>
                  <option value="REPORT_CONFIGURATION">
                    REPORT_CONFIGURATION (Analytics Export)
                  </option>
                  <option value="NOTIFICATION_PROVIDER">
                    NOTIFICATION_PROVIDER (Dispatch Gateway)
                  </option>
                </select>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    color: "#334155",
                    marginBottom: "0.35rem",
                  }}
                >
                  Target Record Code
                </label>
                <select
                  value={testCode}
                  onChange={(e) => setTestCode(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.85rem",
                    border: "1px solid #cbd5e1",
                    borderRadius: 8,
                    fontSize: "0.875rem",
                  }}
                >
                  {(
                    props.masterOptions?.[testType] ??
                    DEFAULT_MASTER_ENTITY_OPTIONS[testType] ??
                    []
                  ).map((opt) => {
                    const val = String(
                      (opt as any).code ?? (opt as any).value ?? "",
                    );
                    const lbl = String(
                      (opt as any).label ?? (opt as any).name ?? val,
                    );
                    return (
                      <option key={val} value={val}>
                        {lbl}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  color: "#334155",
                  marginBottom: "0.35rem",
                }}
              >
                Target Lifecycle Operation
              </label>
              <select
                value={testOp}
                onChange={(e) => setTestOp(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.6rem 0.85rem",
                  border: "1px solid #cbd5e1",
                  borderRadius: 8,
                  fontSize: "0.875rem",
                }}
              >
                <option value="DEACTIVATE_CHECK">
                  DEACTIVATE_CHECK (Attempt Deactivation)
                </option>
                <option value="DELETE_CHECK">
                  DELETE_CHECK (Attempt Master Record Deletion)
                </option>
                <option value="ACTIVATION_CHECK">
                  ACTIVATION_CHECK (Attempt Production Version Activation)
                </option>
                <option value="ROLLBACK_CHECK">
                  ROLLBACK_CHECK (Attempt Rollback to Snapshot)
                </option>
              </select>
            </div>

            <button
              type="button"
              className="cmts-sod-btn cmts-sod-btn--accent"
              style={{ alignSelf: "flex-start" }}
              onClick={runSimulation}
            >
              <FiPlay /> Run Impact Evaluation
            </button>

            {simResult && (
              <div
                style={{
                  padding: "1rem 1.25rem",
                  borderRadius: 12,
                  background:
                    simResult.status === "blocked"
                      ? "#fff1f2"
                      : simResult.status === "warn"
                        ? "#fffbeb"
                        : "#f0fdf4",
                  border: `1px solid ${
                    simResult.status === "blocked"
                      ? "#fecdd3"
                      : simResult.status === "warn"
                        ? "#fde68a"
                        : "#bbf7d0"
                  }`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    marginBottom: "0.35rem",
                  }}
                >
                  {simResult.status === "blocked" ? (
                    <FiLock style={{ color: "#be123c" }} />
                  ) : simResult.status === "warn" ? (
                    <FiAlertTriangle style={{ color: "#c2410c" }} />
                  ) : (
                    <FiCheckCircle style={{ color: "#166534" }} />
                  )}
                  <strong
                    style={{
                      color:
                        simResult.status === "blocked"
                          ? "#9f1239"
                          : simResult.status === "warn"
                            ? "#9a3412"
                            : "#166534",
                    }}
                  >
                    {simResult.title}
                  </strong>
                </div>
                <p
                  style={{ margin: 0, fontSize: "0.875rem", color: "#334155" }}
                >
                  {simResult.message}
                </p>
                {simResult.remediation && (
                  <div
                    style={{
                      marginTop: "0.75rem",
                      paddingTop: "0.75rem",
                      borderTop: "1px dashed #cbd5e1",
                      fontSize: "0.82rem",
                      color: "#475569",
                    }}
                  >
                    <strong>Remediation Recommendation:</strong>{" "}
                    {simResult.remediation}
                  </div>
                )}
              </div>
            )}
          </div>
        </CmtsEnterpriseModal>

        {/* =========================================================================
            Remediation Modal
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={remediateModalOpen}
          title="Dependency Remediation Guidance"
          subtitle={
            remediateRow ? `${remediateRow.code} • ${remediateRow.policy}` : ""
          }
          size="md"
          onClose={() => setRemediateModalOpen(false)}
        >
          {remediateRow && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1.25rem",
                padding: "0.5rem",
              }}
            >
              <div
                style={{
                  padding: "1rem",
                  background: "#f8fafc",
                  borderRadius: 10,
                  border: "1px solid #e2e8f0",
                }}
              >
                <span
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: "#64748b",
                    textTransform: "uppercase",
                  }}
                >
                  Blocking Condition Summary
                </span>
                <p
                  style={{
                    margin: "0.35rem 0 0",
                    fontSize: "0.875rem",
                    color: "#0f172a",
                    fontWeight: 600,
                  }}
                >
                  {remediateRow.reason}
                </p>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    color: "#334155",
                    marginBottom: "0.35rem",
                  }}
                >
                  Recommended Remediation Action
                </label>
                <div
                  style={{
                    padding: "0.85rem 1rem",
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    borderRadius: 8,
                    color: "#1e40af",
                    fontSize: "0.875rem",
                  }}
                >
                  <FiTool style={{ marginRight: 6 }} />
                  {remediateRow.remediation}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "0.75rem",
                  marginTop: "0.5rem",
                }}
              >
                <button
                  type="button"
                  className="cmts-sod-btn cmts-sod-btn--secondary"
                  onClick={() => setRemediateModalOpen(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="cmts-sod-btn cmts-sod-btn--primary"
                  disabled={busy}
                  onClick={() => void handleExecuteRemediation(remediateRow)}
                >
                  {busy ? "Applying Mitigation..." : "Apply Remediation"}
                </button>
              </div>
            </div>
          )}
        </CmtsEnterpriseModal>

        {/* =========================================================================
            Graph Trace Modal
           ========================================================================= */}
        <CmtsEnterpriseModal
          open={graphModalOpen}
          title="Dependency Graph & Node Trace"
          subtitle={
            actionsFor
              ? `${actionsFor.code} • ${actionsFor.policy}`
              : "Master Entity Closure"
          }
          size="lg"
          onClose={() => setGraphModalOpen(false)}
        >
          {actionsFor && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                padding: "0.5rem",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "1rem",
                }}
              >
                <div
                  style={{
                    padding: "0.85rem",
                    background: "#f8fafc",
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "#64748b",
                      fontWeight: 700,
                    }}
                  >
                    SOURCE NODE
                  </span>
                  <p style={{ margin: "0.25rem 0 0", fontWeight: 600 }}>
                    {actionsFor.sourceEntity}
                  </p>
                </div>
                <div
                  style={{
                    padding: "0.85rem",
                    background: "#f8fafc",
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "#64748b",
                      fontWeight: 700,
                    }}
                  >
                    TARGET DEPENDENCY
                  </span>
                  <p style={{ margin: "0.25rem 0 0", fontWeight: 600 }}>
                    {actionsFor.targetEntity}
                  </p>
                </div>
              </div>

              <div
                style={{
                  padding: "0.85rem",
                  background: "#f8fafc",
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                }}
              >
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "#64748b",
                    fontWeight: 700,
                  }}
                >
                  API ENDPOINT CONTRACT
                </span>
                <p
                  style={{
                    margin: "0.25rem 0 0",
                    fontFamily: "monospace",
                    fontSize: "0.82rem",
                    color: "#0284c7",
                  }}
                >
                  {actionsFor.endpoint}
                </p>
              </div>

              <div
                style={{
                  padding: "0.85rem",
                  background: "#f8fafc",
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                }}
              >
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "#64748b",
                    fontWeight: 700,
                  }}
                >
                  CLOSURE & IMPACT ANALYSIS
                </span>
                <p
                  style={{
                    margin: "0.25rem 0 0",
                    fontSize: "0.85rem",
                    color: "#334155",
                  }}
                >
                  {actionsFor.reason}
                </p>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "0.5rem",
                }}
              >
                <button
                  type="button"
                  className="cmts-sod-btn cmts-sod-btn--primary"
                  onClick={() => setGraphModalOpen(false)}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </CmtsEnterpriseModal>
      </div>
    </PageContainer>
  );
}
